import { useState, useEffect } from 'react'
import { Box, Tooltip, Link as MuiLink, Switch } from '@mui/material'
import { useAppServices } from 'hook/services'
import EditLinkModal from '../../EditLinkModal'

const AppData = () => {
  const [posts, setPosts] = useState([])
  const [loader, setLoader] = useState(false)
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({ page: 1, limit: 24, total: 0 })
  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] })
  const AppService = useAppServices()

  const fetchPosts = async (page = 1, limit = 24, q = '') => {
    setLoader(true)
    try {
      const query = `page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`
      const { response } = await AppService.linkinbio.posts({ query })
      if (response) {
        setPosts(response.data || [])
        setPagination({
          page: response.page || page,
          limit: response.limit || limit,
          totalPages: response.pages || Math.ceil((response.total || 0) / (response.limit || limit)),
          totalUsers: response.total || 0,
        })
      }
    } catch (err) {
      console.error('Failed to fetch link-in-bio posts', err)
    } finally {
      setLoader(false)
    }
  }

  // Post ko bio page par show/hide — optimistic update, fail par revert
  const handleToggle = async (mediaId, active) => {
    setPosts((prev) => prev.map((x) => (x.mediaId === mediaId ? { ...x, active } : x)))
    const res = await AppService.linkinbio.toggle({ payload: { mediaId, active } })
    if (res?.error) {
      setPosts((prev) => prev.map((x) => (x.mediaId === mediaId ? { ...x, active: !active } : x)))
    }
  }

  useEffect(() => {
    fetchPosts()
  }, [])

  const handleRefresh = () => fetchPosts(pagination.page, pagination.limit, search)
  const handlePageChange = (page) => fetchPosts(page, pagination.limit, search)
  const handleLimitChange = (limit) => fetchPosts(1, limit, search)
  const handleSearch = () => fetchPosts(1, pagination.limit, search)

  useEffect(() => {
    const columns = [
      { Header: 'Image', accessor: 'image', width: '8%', disableSortBy: true },
      { Header: 'Caption', accessor: 'caption', width: '30%' },
      { Header: 'Link', accessor: 'link', width: '32%' },
      { Header: 'Date', accessor: 'date', width: '10%' },
      { Header: 'Active', accessor: 'active', width: '10%', align: 'center', disableSortBy: true },
      { Header: 'Edit', accessor: 'edit', width: '8%', align: 'center', disableSortBy: true },
    ]

    const rows = posts.map((p) => ({
      image: p.imageUrl ? (
        <Box
          component="img"
          src={p.imageUrl}
          alt="post"
          sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover' }}
        />
      ) : (
        <span style={{ color: '#bbb' }}>—</span>
      ),
      caption: (
        <Box sx={{ maxWidth: 340 }}>
          <Tooltip title={p.caption || ''}>
            <span style={{ fontSize: '0.8rem' }}>
              {(p.caption || '').slice(0, 90)}
              {(p.caption || '').length > 90 ? '…' : ''}
            </span>
          </Tooltip>
        </Box>
      ),
      link: p.linkUrl ? (
        <Tooltip title={p.linkUrl}>
          <MuiLink
            href={p.linkUrl}
            target="_blank"
            rel="noopener"
            sx={{
              display: 'block',
              maxWidth: 360,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.72rem',
            }}
          >
            {p.linkUrl}
          </MuiLink>
        </Tooltip>
      ) : (
        <span style={{ color: '#bbb' }}>—</span>
      ),
      date: (
        <span style={{ fontSize: '0.72rem', color: '#555' }}>
          {p.postedTime
            ? new Date(p.postedTime).toLocaleDateString('en-AU', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '—'}
        </span>
      ),
      active: (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title={p.active !== false ? 'Showing on bio page' : 'Hidden'}>
            <Switch
              checked={p.active !== false}
              color="success"
              onChange={(e) => handleToggle(p.mediaId, e.target.checked)}
            />
          </Tooltip>
        </Box>
      ),
      edit: <EditLinkModal post={p} handleRefresh={handleRefresh} />,
    }))

    setDataTableData({ columns, rows })
  }, [posts])

  return {
    dataTableData,
    handleRefresh,
    loader,
    pagination,
    handlePageChange,
    handleLimitChange,
    search,
    setSearch,
    handleSearch,
  }
}

export default AppData
